import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Save, Plus, Trash2, Eye, EyeOff, Clock, Copy } from "lucide-react";
import DropZone from "../components/DropZone";

function resolveSrc(u) {
  if (!u) return "";
  if (u.startsWith("http")) return u;
  if (u.startsWith("/api/media/")) return `${process.env.REACT_APP_BACKEND_URL}${u}`;
  return u;
}

let _pid = 0;
const newId = () => `popup_${Date.now()}_${++_pid}`;

const EMPTY_POPUP = {
  enabled: true,
  delay_seconds: 10,
  pages: "all",
  eyebrow: "",
  title: "",
  body: "",
  image: "",
  video_url: "",
  media_type: "image", // "image" | "video"
  cta_label: "",
  cta_url: "",
  cta_bg: "#D4FF00",
  cta_color: "#000000",
  footnote: "",
};

const withId = (o) => ({ ...o, id: o.id || newId() });

export default function PopupsEditor() {
  const [items, setItems] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/content/kv/popups").then(({ data }) => Array.isArray(data) && setItems(data.map(withId)));
  }, []);

  const upd = (i, k, v) => setItems((arr) => { const a = [...arr]; a[i] = { ...a[i], [k]: v }; return a; });
  const add = () => setItems((arr) => [...arr, withId({ ...EMPTY_POPUP })]);
  const remove = (i) => setItems((arr) => arr.filter((_, j) => j !== i));
  const dup = (i) => setItems((arr) => {
    const copy = withId({ ...arr[i], id: undefined, title: (arr[i].title || "") + " (cópia)" });
    const next = [...arr]; next.splice(i + 1, 0, copy); return next;
  });

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/content/kv/popups", { value: items });
      toast.success("Popups salvos.");
    } catch { toast.error("Erro ao salvar."); }
    finally { setSaving(false); }
  };

  const preset = (kind) => {
    if (kind === "consultancy") {
      setItems((arr) => [...arr, withId({
        ...EMPTY_POPUP,
        delay_seconds: 10,
        eyebrow: "Consultoria Gratuita",
        title: "Agende uma consultoria grátis com nosso Diretor",
        body: "30 minutos com o Diretor da Places para diagnóstico do seu projeto — sem compromisso.",
        image: "https://images.unsplash.com/photo-1552664730-d307ca884978?w=1200&q=85",
        cta_label: "Quero agendar",
        cta_url: "/contato?intent=client&via=popup-consultoria",
        cta_bg: "#00E59B",
        cta_color: "#000000",
      })]);
    } else if (kind === "discount") {
      setItems((arr) => [...arr, withId({
        ...EMPTY_POPUP,
        delay_seconds: 45,
        eyebrow: "Oportunidade — apenas até amanhã",
        title: "10% de desconto no seu projeto de marketing com a Places",
        body: "Você está interessado em fazer um projeto de marketing com a Places? Aproveite: 10% de desconto somente até amanhã.",
        image: "https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=1200&q=85",
        cta_label: "Garantir desconto",
        cta_url: "/contato?intent=client&via=popup-desconto",
        cta_bg: "#FF5E00",
        cta_color: "#FFFFFF",
        footnote: "Válido para novos contratos até amanhã, 23h59.",
      })]);
    }
  };

  return (
    <div>
      <div className="flex justify-between items-end mb-6 flex-wrap gap-4">
        <div>
          <h1 className="font-display text-4xl">Popups por tempo</h1>
          <p className="text-sm text-neutral-500 mt-1 max-w-xl">
            Popups aparecem após X segundos de navegação, escurecem levemente o site atrás e são mostrados no máximo uma vez por sessão do visitante.
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button onClick={() => preset("consultancy")} data-testid="preset-consultancy" className="text-xs bg-accent-green text-black px-3 py-2 rounded-full font-semibold hover:bg-accent-yellow transition-colors"><Plus className="w-3 h-3 inline" /> Consultoria (10s)</button>
          <button onClick={() => preset("discount")} data-testid="preset-discount" className="text-xs bg-accent-orange text-white px-3 py-2 rounded-full font-semibold hover:bg-black transition-colors"><Plus className="w-3 h-3 inline" /> Desconto (45s)</button>
          <button onClick={add} data-testid="popup-add" className="text-xs bg-black text-white px-3 py-2 rounded-full font-semibold hover:bg-accent-yellow hover:text-black transition-colors"><Plus className="w-3 h-3 inline" /> Novo popup</button>
          <button onClick={save} disabled={saving} data-testid="popups-save" className="bg-black text-white text-sm px-5 py-2.5 rounded-full hover:bg-accent-orange transition-colors disabled:opacity-50"><Save className="w-3 h-3 inline mr-1" /> {saving ? "Salvando…" : "Salvar"}</button>
        </div>
      </div>

      {items.length === 0 && (
        <div className="border border-dashed border-black/15 rounded-md p-10 text-center text-sm text-neutral-500">
          Sem popups configurados. Use os presets acima ou clique em <b>Novo popup</b> para começar.
        </div>
      )}

      <div className="space-y-6">
        {items.map((p, i) => (
          <section key={p.id} className="border border-black/10 rounded-md p-5" data-testid={`popup-item-${i}`}>
            <div className="flex flex-wrap items-center gap-3 mb-4">
              <button onClick={() => upd(i, "enabled", !p.enabled)} className={`text-xs px-3 py-1.5 rounded-full font-semibold transition-colors ${p.enabled ? "bg-accent-green text-black" : "bg-neutral-200 text-neutral-500"}`} data-testid={`popup-toggle-${i}`}>
                {p.enabled ? (<><Eye className="w-3 h-3 inline mr-1" /> Ativo</>) : (<><EyeOff className="w-3 h-3 inline mr-1" /> Pausado</>)}
              </button>
              <label className="text-xs flex items-center gap-1 text-neutral-500">
                <Clock className="w-3 h-3" /> Delay:
                <input type="number" min={0} max={600} value={p.delay_seconds || 0} onChange={(e) => upd(i, "delay_seconds", Number(e.target.value))} className="w-20 border border-black/15 px-2 py-1 rounded text-sm" data-testid={`popup-delay-${i}`} />
                seg.
              </label>
              <label className="text-xs flex items-center gap-1 text-neutral-500">
                Páginas:
                <input value={p.pages || "all"} onChange={(e) => upd(i, "pages", e.target.value)} placeholder="all | home | /cases,/insights" className="w-64 border border-black/15 px-2 py-1 rounded text-sm font-mono" data-testid={`popup-pages-${i}`} />
              </label>
              <div className="ml-auto flex gap-2">
                <button onClick={() => dup(i)} className="text-xs text-neutral-500 hover:text-black transition-colors" title="Duplicar"><Copy className="w-4 h-4" /></button>
                <button onClick={() => remove(i)} className="text-xs text-red-500 hover:text-red-700 transition-colors" title="Excluir"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-3">
                <F label="Eyebrow (pequena legenda superior)" v={p.eyebrow} on={(v) => upd(i, "eyebrow", v)} testid={`popup-eyebrow-${i}`} />
                <F label="Título" v={p.title} on={(v) => upd(i, "title", v)} testid={`popup-title-${i}`} />
                <T label="Corpo (parágrafo)" v={p.body} on={(v) => upd(i, "body", v)} rows={3} testid={`popup-body-${i}`} />
                <div>
                  <label className="text-xs text-neutral-500">Mídia (meio do popup, 16:9)</label>
                  <div className="flex gap-2 mt-1 mb-2">
                    {["image", "video"].map((mt) => (
                      <button key={mt} type="button" onClick={() => upd(i, "media_type", mt)}
                        className={`text-[11px] px-3 py-1 rounded-full transition ${(p.media_type || "image") === mt ? "bg-black text-white" : "border border-black/15 hover:bg-black/5"}`}
                        data-testid={`popup-media-type-${i}-${mt}`}>
                        {mt === "image" ? "Imagem" : "Vídeo"}
                      </button>
                    ))}
                  </div>
                  {(p.media_type || "image") === "image" ? (
                    <>
                      {p.image && <img src={resolveSrc(p.image)} alt="" className="w-full aspect-video object-cover rounded my-2 border border-black/10" />}
                      <DropZone multiple={false} onUploaded={(f) => upd(i, "image", f.url)} />
                      <input value={p.image || ""} onChange={(e) => upd(i, "image", e.target.value)} placeholder="ou URL" className="w-full text-xs border border-black/15 px-2 py-1.5 rounded mt-1" data-testid={`popup-image-${i}`} />
                    </>
                  ) : (
                    <>
                      <input value={p.video_url || ""} onChange={(e) => upd(i, "video_url", e.target.value)} placeholder="URL do YouTube ou upload"
                        className="w-full text-xs border border-black/15 px-2 py-1.5 rounded" data-testid={`popup-video-url-${i}`} />
                      <div className="my-2 text-[11px] text-neutral-500">Ou faça upload direto (MP4/WebM até 100MB):</div>
                      <DropZone multiple={false} onUploaded={(f) => upd(i, "video_url", f.url)} />
                      <div className="mt-1 text-[10px] text-neutral-400">Suporta: YouTube, Vimeo, ou vídeo próprio</div>
                    </>
                  )}
                </div>
              </div>
              <div className="space-y-3">
                <F label="Texto do botão (CTA)" v={p.cta_label} on={(v) => upd(i, "cta_label", v)} testid={`popup-cta-label-${i}`} />
                <F label="Link do botão (URL absoluta ou path como /contato)" v={p.cta_url} on={(v) => upd(i, "cta_url", v)} testid={`popup-cta-url-${i}`} />
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-neutral-500">Cor de fundo do botão</label>
                    <div className="flex gap-2 items-center">
                      <input type="color" value={p.cta_bg || "#D4FF00"} onChange={(e) => upd(i, "cta_bg", e.target.value)} className="w-10 h-10 rounded cursor-pointer" data-testid={`popup-cta-bg-${i}`} />
                      <input value={p.cta_bg || "#D4FF00"} onChange={(e) => upd(i, "cta_bg", e.target.value)} className="flex-1 border border-black/15 px-2 py-1.5 rounded text-sm font-mono" />
                    </div>
                  </div>
                  <div>
                    <label className="text-xs text-neutral-500">Cor do texto do botão</label>
                    <div className="flex gap-2 items-center">
                      <input type="color" value={p.cta_color || "#000000"} onChange={(e) => upd(i, "cta_color", e.target.value)} className="w-10 h-10 rounded cursor-pointer" data-testid={`popup-cta-color-${i}`} />
                      <input value={p.cta_color || "#000000"} onChange={(e) => upd(i, "cta_color", e.target.value)} className="flex-1 border border-black/15 px-2 py-1.5 rounded text-sm font-mono" />
                    </div>
                  </div>
                </div>
                <F label="Rodapé (letra pequena — ex: 'Válido apenas hoje')" v={p.footnote} on={(v) => upd(i, "footnote", v)} testid={`popup-footnote-${i}`} />
                {/* Preview do CTA */}
                {p.cta_label && (
                  <div className="border border-dashed border-black/15 rounded p-3">
                    <div className="text-[10px] text-neutral-400 uppercase mb-2">Preview do botão</div>
                    <div className="inline-flex px-6 py-3 rounded-full font-semibold text-sm" style={{ background: p.cta_bg, color: p.cta_color }}>{p.cta_label}</div>
                  </div>
                )}
              </div>
            </div>
          </section>
        ))}
      </div>
    </div>
  );
}

function F({ label, v, on, testid }) {
  return (<div><label className="text-xs text-neutral-500">{label}</label><input value={v ?? ""} onChange={(e) => on(e.target.value)} data-testid={testid} className="w-full border border-black/15 px-3 py-2 rounded text-sm" /></div>);
}
function T({ label, v, on, rows = 3, testid }) {
  return (<div><label className="text-xs text-neutral-500">{label}</label><textarea value={v ?? ""} onChange={(e) => on(e.target.value)} rows={rows} data-testid={testid} className="w-full border border-black/15 px-3 py-2 rounded text-sm" /></div>);
}
